import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../context/AuthContext';
import {
  approveCalendarInvite,
  declineCalendarInvite,
  fetchUpcomingEvents,
  type CalendarEventItem,
} from '../../../lib/calendarApi';

function fmt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ro-RO', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export default function AppointmentsCard() {
  const navigate = useNavigate();
  const { me } = useAuth();

  const [items, setItems] = useState<CalendarEventItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchUpcomingEvents(6);
      setItems(data);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const data = await fetchUpcomingEvents(6);
        if (!cancelled) setItems(data);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    const t = window.setInterval(() => void run(), 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, []);

  const myId = Number(me?.id);
  const withMyStatus = useMemo(() => {
    return items
      .map((ev) => {
        const p = Array.isArray(ev.participants) ? ev.participants.find((x) => Number(x.user_id) === myId) : null;
        return { ev, me: p || null };
      })
      .filter((x) => Boolean(x.me)); // only events where I'm invited/participant
  }, [items, myId]);

  const act = async (eventId: number, action: 'approve' | 'decline') => {
    setBusyId(eventId);
    try {
      if (action === 'approve') await approveCalendarInvite(eventId);
      else await declineCalendarInvite(eventId);
      await load();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Icon name="CalendarDays" size={18} className="text-muted-foreground" />
          <div className="font-semibold text-foreground">Programări</div>
        </div>
        <Button variant="ghost" size="sm" iconName="ExternalLink" onClick={() => navigate('/calendar')}>
          Vezi calendar
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground mt-2">Se incarca...</div>
      ) : withMyStatus.length === 0 ? (
        <div className="text-sm text-muted-foreground mt-2">Nu exista programari viitoare pentru tine.</div>
      ) : (
        <div className="mt-3 space-y-3">
          {withMyStatus.map(({ ev, me: part }) => {
            const response = ((part as any)?.response || (part?.approved ? 'accepted' : 'pending')) as string;
            const pending = response === 'pending';
            return (
              <div key={ev.id} className="border border-border rounded-md p-3 bg-background">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-sm font-medium text-foreground truncate">{ev.title}</div>
                      <span className="text-xs px-2 py-0.5 rounded bg-muted text-foreground">{ev.event_type || 'Altul'}</span>
                      {response === 'pending' ? (
                        <span className="text-xs px-2 py-0.5 rounded bg-warning/20 text-warning border border-warning/30">
                          În așteptare
                        </span>
                      ) : response === 'declined' ? (
                        <span className="text-xs px-2 py-0.5 rounded bg-error/10 text-error border border-error/20">
                          Respins
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded bg-success/15 text-success border border-success/25">
                          Acceptat
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {fmt(ev.start_time)} → {fmt(ev.end_time)}
                    </div>
                    {ev.description && <div className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{ev.description}</div>}
                  </div>

                  {pending && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void act(ev.id, 'decline')}
                        disabled={busyId === ev.id}
                      >
                        Respinge
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => void act(ev.id, 'approve')}
                        disabled={busyId === ev.id}
                      >
                        Acceptă
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
